import type { PayOccurrenceBody } from '@klopotun/contracts';
import { toCivilDate } from '@klopotun/domain';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMeQuery } from '../../entities/me/index.js';
import { useOccurrenceQuery, usePayOccurrenceMutation } from '../../entities/occurrence/index.js';
import { paymentQueries } from '../../entities/payment/index.js';
import {
  ConfirmPaymentForm,
  toConfirmPaymentValues,
} from '../../features/confirm-payment/index.js';
import { formatExpectedAmount } from '../../features/occurrence-view/index.js';
import { formatStoredDateForPeople } from '../../features/payment-view/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import { ErrorState, List, ListItem, Screen, Spinner } from '../../shared/ui/index.js';
import styles from './occurrence-pay-page.module.css';

const LoadingScreen = () => (
  <Screen>
    <div className={styles.centered}>
      <Spinner label="Завантаження" />
    </div>
  </Screen>
);

type FailureScreenProps = {
  message: string;
  onRetry: () => void;
};

const FailureScreen = ({ message, onRetry }: FailureScreenProps) => (
  <Screen>
    <ErrorState title="Не вдалося завантажити період" message={message} onRetry={onRetry} />
  </Screen>
);

export const OccurrencePayPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const occurrence = useOccurrenceQuery(id);
  const me = useMeQuery();
  const paymentId = occurrence.data?.paymentId ?? '';
  const payment = useQuery({ ...paymentQueries.detail(paymentId), enabled: paymentId !== '' });
  const payOccurrence = usePayOccurrenceMutation();
  const goBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  useBackButton(goBack);

  const failure = occurrence.error ?? me.error ?? payment.error;
  const loadedOccurrence = occurrence.data;
  const loadedSettings = me.data?.settings;
  const loadedPayment = payment.data;

  if (failure !== null) {
    return (
      <FailureScreen
        message={failure.message}
        onRetry={() => {
          void occurrence.refetch();
          void me.refetch();
          void payment.refetch();
        }}
      />
    );
  }

  if (
    loadedOccurrence === undefined ||
    loadedSettings === undefined ||
    loadedPayment === undefined
  ) {
    return <LoadingScreen />;
  }

  const today = toCivilDate(new Date(), loadedSettings.timezone);
  const isCorrection = loadedOccurrence.status === 'paid';

  const confirm = (body: PayOccurrenceBody): void => {
    payOccurrence.mutate(
      { id, body },
      {
        onSuccess: () => {
          void navigate(-1);
        },
      },
    );
  };

  return (
    <Screen>
      <div className={styles.content}>
        <h1 className={styles.title}>
          {isCorrection ? 'Виправлення оплати' : 'Підтвердження оплати'}
        </h1>

        <List>
          <ListItem title="Підписка" value={loadedPayment.name} />
          <ListItem title="Термін" value={formatStoredDateForPeople(loadedOccurrence.dueDate)} />
          <ListItem title="Сума за планом" value={formatExpectedAmount(loadedOccurrence)} />
        </List>

        {payOccurrence.error !== null && (
          <p className={styles.saveError} role="alert">
            {payOccurrence.error.message}
          </p>
        )}

        <ConfirmPaymentForm
          initialValues={toConfirmPaymentValues(loadedOccurrence, today)}
          amountMode={loadedPayment.amountMode}
          templateAmountMinor={loadedPayment.amountMinor}
          templateCurrency={loadedPayment.currency}
          onSubmit={confirm}
          onCancel={goBack}
          busy={payOccurrence.isPending}
        />
      </div>
    </Screen>
  );
};
